import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Luggage, Clock, MapPin, Minus, Plus, ShieldCheck, Refrigerator, CreditCard } from "lucide-react";

const BAG = "https://images.unsplash.com/photo-1768668053205-103e18fc042d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function LuggageStorage() {
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", date: "", arrival_time: "", end_time: "" });
  const [bags, setBags] = useState(1);
  const [price, setPrice] = useState(5);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({ dates: [], hours: {}, default_open: "09:00", default_close: "17:00" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/luggage/price").then((r) => setPrice(r.data.price_per_bag)).catch(() => {});
    api.get("/luggage/blocked-dates").then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);
  const total = (bags * price).toFixed(2);
  const dateBlocked = f.date && (cfg.dates || []).includes(f.date);
  const dayOpen = (f.date && cfg.hours?.[f.date]?.open) || cfg.default_open || "09:00";
  const dayClose = (f.date && cfg.hours?.[f.date]?.close) || cfg.default_close || "17:00";
  const timesOk = (!f.arrival_time || f.arrival_time >= dayOpen) && (!f.end_time || f.end_time <= dayClose) && (!f.arrival_time || !f.end_time || f.end_time > f.arrival_time);
  const valid = f.first_name && f.last_name && f.phone && f.email && f.date && !dateBlocked && bags >= 1 && timesOk;

  const pay = async () => {
    if (f.end_time && f.end_time > dayClose) { toast.error(`L'heure de fin ne peut pas dépasser ${dayClose}.`); return; }
    if (f.arrival_time && f.arrival_time < dayOpen) { toast.error(`L'heure d'arrivée ne peut pas être avant ${dayOpen}.`); return; }
    if (f.arrival_time && f.end_time && f.end_time <= f.arrival_time) { toast.error("L'heure de fin doit être après l'heure d'arrivée."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/luggage/bookings", { ...f, bags, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setLoading(false); }
  };

  return (
    <Layout>
      <Seo title="Consigne à bagages à Rouen | Près de la gare | NJ Conciergerie"
        description="Consigne à bagages sécurisée à Rouen, près de la gare. Déposez vos valises de 9h à 17h à partir de 5€ par bagage. Réservation et paiement en ligne."
        canonical="/consigne" jsonLd={useLocalBusinessLd()} />
      <section className="relative h-[40vh] flex items-center">
        <img src={BAG} alt="Consigne à bagages Rouen" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-nj-dark/55" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Service · Rouen</span>
          <h1 className="font-serif font-light text-white text-4xl sm:text-5xl mt-3">Consigne à bagages</h1>
          <p className="text-white/85 mt-2">Déposez vos bagages en toute sécurité — {price}€ par bagage, de 9h à 17h.</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="flex items-center gap-3 bg-nj-olive/40 rounded-md p-4"><Clock className="w-5 h-5 text-nj-primary" /><span className="text-sm text-nj-text2">9h00 – 17h00</span></div>
          <div className="flex items-center gap-3 bg-nj-olive/40 rounded-md p-4"><Luggage className="w-5 h-5 text-nj-primary" /><span className="text-sm text-nj-text2">{price}€ / bagage</span></div>
          <div className="flex items-center gap-3 bg-nj-olive/40 rounded-md p-4"><MapPin className="w-5 h-5 text-nj-primary" /><span className="text-sm text-nj-text2">22 bd de la Marne</span></div>
        </div>

        <div className="flex items-start gap-3 bg-nj-gold/15 border border-nj-gold/40 rounded-md p-4 mb-8" data-testid="fridge-info">
          <Refrigerator className="w-5 h-5 text-nj-primary shrink-0 mt-0.5" />
          <span className="text-sm text-nj-text2"><strong className="text-nj-text">Produits frais ?</strong> Des frigos sont disponibles sur place pour conserver vos aliments et boissons pendant votre journée.</span>
        </div>

        <div className="bg-white border border-nj-olive rounded-lg p-7" data-testid="luggage-form">
          <h2 className="font-serif text-2xl text-nj-text mb-6">Réserver ma consigne</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label>Prénom *</Label><Input data-testid="lug-firstname" value={f.first_name} onChange={set("first_name")} className="mt-1.5" /></div>
            <div><Label>Nom *</Label><Input data-testid="lug-lastname" value={f.last_name} onChange={set("last_name")} className="mt-1.5" /></div>
            <div><Label>Téléphone *</Label><Input data-testid="lug-phone" value={f.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Email *</Label><Input type="email" data-testid="lug-email" value={f.email} onChange={set("email")} className="mt-1.5" /></div>
            <div><Label>Date *</Label><Input type="date" data-testid="lug-date" min={new Date().toISOString().slice(0, 10)} value={f.date} onChange={set("date")} className="mt-1.5" />
              {dateBlocked
                ? <p data-testid="lug-date-blocked" className="text-xs text-red-600 mt-1.5">Consigne fermée ce jour — merci de choisir une autre date.</p>
                : f.date && <p data-testid="lug-date-hours" className="text-xs text-nj-muted mt-1.5">Horaires ce jour : {dayOpen} – {dayClose}</p>}
            </div>
            <div><Label>Heure d'arrivée souhaitée</Label><Input type="time" data-testid="lug-arrival" min={dayOpen} max={dayClose} value={f.arrival_time} onChange={set("arrival_time")} className="mt-1.5" /></div>
            <div><Label>Heure de fin souhaitée</Label><Input type="time" data-testid="lug-end" min={dayOpen} max={dayClose} step="900" value={f.end_time} onChange={set("end_time")} className="mt-1.5" /></div>
            <div>
              <Label>Nombre de bagages *</Label>
              <div className="flex items-center gap-3 mt-2">
                <button type="button" data-testid="lug-minus" onClick={() => setBags(Math.max(1, bags - 1))} className="w-9 h-9 rounded-full border border-nj-olive flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-semibold text-lg" data-testid="lug-bags">{bags}</span>
                <button type="button" data-testid="lug-plus" onClick={() => setBags(Math.min(20, bags + 1))} className="w-9 h-9 rounded-full border border-nj-olive flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-7 pt-5 border-t border-nj-olive">
            <span className="text-nj-text2">Total</span>
            <span className="text-2xl font-semibold text-nj-text" data-testid="lug-total">{total}€</span>
          </div>
          <Button onClick={pay} disabled={!valid || loading} data-testid="lug-pay" className="w-full mt-5 bg-nj-primary hover:bg-nj-primaryHover text-white rounded-sm py-6 text-base">
            {loading ? "Redirection…" : `Payer ${total}€ en toute sécurité`}
          </Button>
          <p className="text-xs text-nj-muted mt-3 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Paiement sécurisé par Stripe. Confirmation par email.</p>
          <p className="text-xs text-nj-text2 mt-2 flex items-center gap-1.5" data-testid="onsite-payment-info"><CreditCard className="w-3.5 h-3.5 text-nj-primary" /> Vous pouvez aussi régler <strong>sur place</strong>, par carte bancaire ou en espèces.</p>
        </div>
      </div>
    </Layout>
  );
}
